<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WhatsappConnectionsAPI from 'dashboard/api/whatsappConnections';

const { t } = useI18n();

const connections = ref([]);
const loading = ref(false);
const syncing = ref(false);
const syncingInbox = ref(null);

onMounted(() => {
  fetchConnections();
});

const fetchConnections = async () => {
  loading.value = true;
  try {
    const response = await WhatsappConnectionsAPI.getAll();
    connections.value = response.data;
  } catch (error) {
    useAlert(t('WHATSAPP_CONNECTIONS.FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

const syncAll = async () => {
  syncing.value = true;
  try {
    const response = await WhatsappConnectionsAPI.syncAll();
    useAlert(t('WHATSAPP_CONNECTIONS.SYNC_ALL_SUCCESS', { count: response.data.synced_count }));
    await fetchConnections();
  } catch (error) {
    useAlert(t('WHATSAPP_CONNECTIONS.SYNC_ALL_ERROR'));
  } finally {
    syncing.value = false;
  }
};

const syncSingle = async (inboxId) => {
  syncingInbox.value = inboxId;
  try {
    await WhatsappConnectionsAPI.sync(inboxId);
    useAlert(t('WHATSAPP_CONNECTIONS.SYNC_SUCCESS'));
    await fetchConnections();
  } catch (error) {
    useAlert(t('WHATSAPP_CONNECTIONS.SYNC_ERROR'));
  } finally {
    syncingInbox.value = null;
  }
};

const deleteConnection = async (id) => {
  try {
    await WhatsappConnectionsAPI.delete(id);
    useAlert(t('WHATSAPP_CONNECTIONS.DELETE_SUCCESS'));
    await fetchConnections();
  } catch (error) {
    useAlert(t('WHATSAPP_CONNECTIONS.DELETE_ERROR'));
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'connected':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'syncing':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    default:
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString();
};

const truncateToken = (token) => {
  if (!token) return '-';
  if (token.length <= 20) return token;
  return `${token.substring(0, 10)}...${token.substring(token.length - 10)}`;
};
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="$t('WHATSAPP_CONNECTIONS.LOADING')"
    :no-records-found="!connections.length"
    :no-records-message="$t('WHATSAPP_CONNECTIONS.NO_CONNECTIONS')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('WHATSAPP_CONNECTIONS.HEADER')"
        :description="$t('WHATSAPP_CONNECTIONS.DESCRIPTION')"
        feature-name="whatsapp_connections"
      >
        <template #actions>
          <Button
            icon="i-lucide-refresh-cw"
            :label="$t('WHATSAPP_CONNECTIONS.SYNC_ALL')"
            :is-loading="syncing"
            @click="syncAll"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-n-weak">
          <thead class="bg-n-alpha-1">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider">
                {{ $t('WHATSAPP_CONNECTIONS.TABLE.INBOX') }}
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider">
                {{ $t('WHATSAPP_CONNECTIONS.TABLE.ACCOUNT') }}
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider">
                {{ $t('WHATSAPP_CONNECTIONS.TABLE.JID') }}
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider">
                {{ $t('WHATSAPP_CONNECTIONS.TABLE.LID') }}
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider">
                {{ $t('WHATSAPP_CONNECTIONS.TABLE.TOKEN') }}
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider">
                {{ $t('WHATSAPP_CONNECTIONS.TABLE.STATUS') }}
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider">
                {{ $t('WHATSAPP_CONNECTIONS.TABLE.LAST_SYNC') }}
              </th>
              <th class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase tracking-wider">
                {{ $t('WHATSAPP_CONNECTIONS.TABLE.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak text-n-slate-11">
            <tr v-for="connection in connections" :key="connection.id">
              <td class="px-4 py-4 whitespace-nowrap">
                <div class="flex flex-col">
                  <span class="font-medium">{{ connection.inbox_name || '-' }}</span>
                  <span class="text-xs text-n-slate-10">ID: {{ connection.inbox_id || '-' }}</span>
                </div>
              </td>
              <td class="px-4 py-4 whitespace-nowrap">
                <div class="flex flex-col">
                  <span class="font-medium">{{ connection.account_name || '-' }}</span>
                  <span class="text-xs text-n-slate-10">ID: {{ connection.account_id || '-' }}</span>
                </div>
              </td>
              <td class="px-4 py-4 whitespace-nowrap">
                <span class="font-mono text-sm">{{ connection.jid || '-' }}</span>
              </td>
              <td class="px-4 py-4 whitespace-nowrap">
                <span class="font-mono text-sm">{{ connection.lid || '-' }}</span>
              </td>
              <td class="px-4 py-4 whitespace-nowrap">
                <span
                  class="font-mono text-sm cursor-pointer"
                  :title="connection.token"
                >
                  {{ truncateToken(connection.token) }}
                </span>
              </td>
              <td class="px-4 py-4 whitespace-nowrap">
                <span
                  class="px-2 py-1 text-xs font-medium rounded-full"
                  :class="getStatusColor(connection.status)"
                >
                  {{ $t(`WHATSAPP_CONNECTIONS.STATUS.${(connection.status || 'disconnected').toUpperCase()}`) }}
                </span>
              </td>
              <td class="px-4 py-4 whitespace-nowrap text-sm">
                {{ formatDate(connection.last_sync_at) }}
              </td>
              <td class="px-4 py-4 whitespace-nowrap text-right">
                <div class="flex justify-end gap-1">
                  <Button
                    v-if="connection.inbox_id"
                    v-tooltip.top="$t('WHATSAPP_CONNECTIONS.SYNC_SINGLE')"
                    icon="i-lucide-refresh-cw"
                    slate
                    xs
                    faded
                    :is-loading="syncingInbox === connection.inbox_id"
                    @click="syncSingle(connection.inbox_id)"
                  />
                  <Button
                    v-tooltip.top="$t('WHATSAPP_CONNECTIONS.DELETE')"
                    icon="i-lucide-trash-2"
                    xs
                    ruby
                    faded
                    @click="deleteConnection(connection.id)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </SettingsLayout>
</template>
